import React, { FC, FormEvent, Fragment, useEffect, useState } from 'react';

import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  TextField,
} from '@mui/material';

import styles from './OverrideSensorNameDialog.module.scss';
import { SensorMappingModel } from '../SensorMappingModel';
import { WordMappingModel } from 'pages/ContextManagerPage/ContextManagementView/WordMappingModel';
import { ContextManagerService } from 'pages/ContextManagerPage/ContextManagerService';
import ConfirmationDialog from 'dialogs/ConfirmationDialog/ConfirmationDialog';
import { AlertConfigModel } from 'dialogs/AlertDialog/AlertConfigModel';
import AlertDialog from 'dialogs/AlertDialog/AlertDialog';

interface OverrideSensorNameDialogProps {
  onClose: () => void;
  onSensorUpdate: () => void;
  sensorMapping: SensorMappingModel;
  wordMappings: WordMappingModel[];
}

const OverrideSensorNameDialog: FC<OverrideSensorNameDialogProps> = (
  {
    onClose,
    onSensorUpdate,
    sensorMapping,
    wordMappings,
  },
) => {

  const [currentAlert, setCurrentAlert] = useState<AlertConfigModel | null>(null);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false);
  const [formData, setFormData] = useState<Record<string, string>>({
    systemCode: '',
    systemSuffix: '',
    subSystemCode: '',
    subSystemSuffix: '',
    componentCode: '',
    componentSuffix: '',
    subComponentCode: '',
    subComponentSuffix: '',
  });
  const [initialFormData, setInitialFormData] = useState<Record<string, string>>({})
  const [isOverrideConfirmationDialogOpen, setIsOverrideConfirmationDialogOpen] = useState<boolean>(false);
  const [hasFormChanged, setHasFormChanged] = useState<boolean>(false);
  const [words, setWords] = useState<string[]>([])

  useEffect(() => {
    setHasFormChanged(JSON.stringify(formData) !== JSON.stringify(initialFormData));
  }, [formData, initialFormData]);

  useEffect(() => {
    const wordsArray = wordMappings.map(mapping => mapping.word);
    setWords(wordsArray);
  }, [wordMappings]);

  useEffect(() => {
    if (sensorMapping.override) {
      populateInitialInputValues(sensorMapping.override);
    }
  }, [sensorMapping]);

  const contextModelLevels: {label: string, name: string, suffixName: string}[] = [
    { label: 'System', name: 'systemCode', suffixName: 'systemSuffix'},
    { label: 'Sub System', name: 'subSystemCode', suffixName: 'subSystemSuffix'},
    { label: 'Component', name: 'componentCode', suffixName: 'componentSuffix'},
    { label: 'Sub Component', name: 'subComponentCode', suffixName: 'subComponentSuffix'},
  ];

  const populateInitialInputValues = (overrideString: string): void => {
    const splitStrings: string[] = overrideString.split(/[-.]/);

    if (splitStrings.length < 8) {
      console.warn('populateInitialInputValues: Invalid input string. Expected at least 8 parts separated by "-" or "."');
    }

    const initialFormData = {
      systemCode: splitStrings[0] || '',
      systemSuffix: splitStrings[1] || '',
      subSystemCode: splitStrings[2] || '',
      subSystemSuffix: splitStrings[3] || '',
      componentCode: splitStrings[4] || '',
      componentSuffix: splitStrings[5] || '',
      subComponentCode: splitStrings[6] || '',
      subComponentSuffix: splitStrings[7] || ''
    }

    setFormData(initialFormData);
    setInitialFormData(initialFormData);
  }

  const cancelOverrideHandler = (): void => {
    setIsOverrideConfirmationDialogOpen(false);
  }

  const closeDialogHandler = (event?: Object, reason?: any): void => {
    if (reason && reason === 'backdropClick') return;

    onClose();
  };

  const confirmOverrideHandler = async (): Promise<void> => {
    setIsOverrideConfirmationDialogOpen(false);
    try {
      const overrideString: string = [
        formatOverrideStringPart(formData.systemCode, formData.systemSuffix),
        formatOverrideStringPart(formData.subSystemCode, formData.subSystemSuffix),
        formatOverrideStringPart(formData.componentCode, formData.componentSuffix),
        formatOverrideStringPart(formData.subComponentCode, formData.subComponentSuffix)
      ].join('.');

      const updateResult = await ContextManagerService.overrideSensorName(sensorMapping.sensorId, overrideString)

      if (!updateResult || updateResult.errors) {
        throw new Error('Error overriding sensor name: ' + updateResult?.errors);
      }

      onSensorUpdate();
      closeDialogHandler();
    } catch (error) {
      console.error('Failed to override sensor name:', error);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed to override sensor name. Please try again, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    }
  }

  const formatOverrideStringPart = (code: string, suffix: string): string => {
    return suffix ? `${code}-${suffix}` : code;
  }

  const formChangeHandler = (fieldName: string) => (event: any, value?: string): void => {
    setFormData({
      ...formData,
      [fieldName]: value || event.target.value
    });
  };

  const formSubmitHandler = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault(); // Prevent the default form submission behavior, which sends a request
    setIsOverrideConfirmationDialogOpen(true);
  }

  return (
    <Fragment>
      <ConfirmationDialog
        open={isOverrideConfirmationDialogOpen}
        title={`Override Sensor Name`}
        message="Please confirm you want to override this sensor name. This override will be used instead of the sensor name when rebuilding models."
        onConfirm={confirmOverrideHandler}
        onCancel={cancelOverrideHandler}
      />

      <Dialog
        className={styles.OverrideSensorNameDialog}
        data-testid="OverrideSensorNameDialog"
        open={true}
        onClose={closeDialogHandler}
      >
        <DialogTitle>Override Sensor Name</DialogTitle>
        <DialogContent>
          <form
            className={styles.sensorOverrideForm}
            onSubmit={formSubmitHandler}
          >
            {contextModelLevels.map((level, index) => (
              <div
                key={index}
                className={styles.overrideFormRow}
              >
                <FormControl
                  className={styles.levelInput}
                  required
                >
                  <Autocomplete
                    options={words}
                    value={words.find(word => word === formData[level.name]) || null}
                    onInputChange={(event: any, value: string) => formChangeHandler(level.name)(event, value)}
                    renderInput={(params) => {
                      return (
                        <TextField {...params} required label={level.label} />
                      )
                    }}
                  />
                </FormControl>

                <div className={styles.formControlDashDivider}>-</div>
                <FormControl className={styles.levelNumberSuffix}>
                  <TextField
                    type="number"
                    placeholder="#"
                    value={formData[level.suffixName]}
                    inputProps={{
                      min: 1,
                      max: 99,
                    }}
                    onChange={formChangeHandler(level.suffixName)}
                  />
                </FormControl>
              </div>
            ))}

            <DialogActions className={styles.formButtons}>
              <Button
                variant="contained"
                color="error"
                onClick={closeDialogHandler}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                color={'primary'}
                variant="contained"
                disabled={!hasFormChanged}
              >
                Confirm
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      { isAlertDialogOpen && currentAlert &&
        <AlertDialog
          onClose={() => {
            setIsAlertDialogOpen(false);
            setCurrentAlert(null);
          }}
          title={currentAlert.title}
          message={currentAlert.message}
        ></AlertDialog>
      }
    </Fragment>
  );
}

export default OverrideSensorNameDialog;
